// Code/src/Components/Documents/DocumentDetailPanel.tsx
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../stores/StoreContext";
import LoadingSpinner from "../../../HelperComponents/LoadingSpinner";
import { getSimpleDateFormat } from "../../../Utility/Utility";
import { FormField } from "../ViewDocs/Documents.types";

/**
 * DocumentDetailPanel Component
 * Displays document details in read-only format
 */
const DocumentDetailPanel: React.FC = observer(() => {
  const { documentStore, uiStore } = useStore();
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [documentMetadata, setDocumentMetadata] = useState<
    Record<string, string | number | boolean | Date | null>
  >({});

  const document = documentStore.selectedDocument;
  const isOpen = uiStore.isViewPanelOpen;

  // Load form schema when panel opens and document is selected
  useEffect(() => {
    if (isOpen && document?.MasterDocumentId) {
      const masterDocumentId = parseInt(document.MasterDocumentId, 10);
      if (masterDocumentId > 0) {
        setIsLoadingSchema(true);

        documentStore
          .fetchDocumentFormTemplate(masterDocumentId)
          .finally(() => {
            setIsLoadingSchema(false);
          });
      }
    }
  }, [isOpen, document?.MasterDocumentId, document?.ID, documentStore]);

  // Extract metadata from document for display
  // Fetch full SharePoint list item metadata (includes custom columns like BackgroundType)
  useEffect(() => {
    const loadDocumentMetadata = async () => {
      if (!document || !documentStore.currentFormSchema) {
        return;
      }

      setIsLoadingMetadata(true);

      try {
        // If document has SharePoint list item info, fetch full metadata from SharePoint
        if (document.ListItemId && document.List) {
          const siteUrl =
            typeof documentStore.currentFormSchema.site === "string"
              ? documentStore.currentFormSchema.site
              : "";

          if (siteUrl && document.List) {
            const libraryName = document.List;

            // Fetch full list item with all fields
            const listItemData =
              await documentStore.fetchDocumentListItemMetadata(
                siteUrl,
                libraryName,
                parseInt(document.ListItemId, 10),
              );

            if (listItemData) {
              // Use the full SharePoint metadata
              setDocumentMetadata(listItemData);

              setIsLoadingMetadata(false);
              return;
            }
          }
        }
      } catch (error) {}

      // Fallback: use basic document properties
      const metadata: Record<string, string | number | boolean | Date | null> =
        { ...document };
      setDocumentMetadata(metadata);
      setIsLoadingMetadata(false);
    };

    if (document && documentStore.currentFormSchema) {
      loadDocumentMetadata();
    }
  }, [document, document?.ID, documentStore, documentStore.currentFormSchema]);

  // Handle panel close
  const handleClose = () => {
    uiStore.closeViewPanel();
    documentStore.clearSelectedDocument();
    documentStore.clearFormSchema();
  };

  // Render field value based on field type
  const renderFieldValue = (
    field: FormField,
    value: string | number | boolean | Date | null,
  ): React.ReactNode => {
    if (value === null || value === undefined || value === "") {
      return <span style={{ color: "hsl(var(--muted-foreground))" }}>N/A</span>;
    }

    switch (field.type) {
      case "Date":
      case "DateTime":
      case "viewOnlyDate":
        return <span>{getSimpleDateFormat(String(value))}</span>;

      case "Boolean":
      case "Checkbox":
        return <span>{value === true || value === "true" ? "Yes" : "No"}</span>;

      case "Number":
        return <span>{String(value)}</span>;

      default:
        return <span>{String(value)}</span>;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "flex-end",
        zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{
          width: 460,
          maxWidth: "90%",
          height: "100%",
          backgroundColor: "hsl(var(--background))",
          color: "hsl(var(--foreground))",
          boxShadow: "-4px 0 16px rgba(0,0,0,0.2)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid var(--app-border-color)",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            {document?.ContentType || "Document Details"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              border: "none",
              background: "hsl(var(--background))",
              color: "hsl(var(--foreground))",
              cursor: "pointer",
              fontSize: 20,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div
          style={{
            padding: "16px",
            overflowY: "auto",
            flex: 1,
            backgroundColor: "hsl(var(--background))",
          }}
        >
          {/* Loading State */}
          {(isLoadingSchema || isLoadingMetadata) && <LoadingSpinner />}

          {!isLoadingSchema && !isLoadingMetadata && !document && (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "hsl(var(--muted-foreground))",
              }}
            >
              No document selected.
            </div>
          )}

          {!isLoadingSchema && !isLoadingMetadata && document && (
            <>
              {/* Form Schema Fields (if available) */}
              {documentStore.currentFormSchema?.fields &&
                documentStore.currentFormSchema.fields.filter(
                  (f) => f.type !== "Internal",
                ).length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <h3
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        marginBottom: 12,
                      }}
                    >
                      Document Details
                    </h3>

                    {documentStore.currentFormSchema.fields
                      .filter((field) => field.type !== "Internal")
                      .map((field) => {
                        let fieldValue: any = null;

                        // Build possible SharePoint field name variations
                        // SharePoint encodes spaces as _x0020_ and may add/remove Id suffix
                        const possibleFieldNames = [
                          field.name, // Original: "BackgroundTypeId"
                          field.name.replace(/Id$/i, ""), // Remove Id: "BackgroundType"
                          field.name
                            .replace(/([A-Z])/g, "_x0020_$1")
                            .replace(/^_x0020_/, ""), // Add space encoding: "Background_x0020_Type_x0020_Id"
                          field.name
                            .replace(/Id$/i, "")
                            .replace(/([A-Z])/g, "_x0020_$1")
                            .replace(/^_x0020_/, ""), // Remove Id + space encoding: "Background_x0020_Type"
                          field.name
                            .replace(/([A-Z])/g, " $1")
                            .trim()
                            .replace(/ /g, "_x0020_"), // CamelCase to space encoding: "Background_x0020_Type_x0020_Id"
                          field.name
                            .replace(/Id$/i, "")
                            .replace(/([A-Z])/g, " $1")
                            .trim()
                            .replace(/ /g, "_x0020_"), // Remove Id + CamelCase to space encoding
                        ];

                        // Try to find field value using possible field names
                        let rawValue: any = null;
                        for (const possibleName of possibleFieldNames) {
                          if (
                            documentMetadata[possibleName] !== undefined &&
                            documentMetadata[possibleName] !== null
                          ) {
                            rawValue = documentMetadata[possibleName];
                            break;
                          }
                        }

                        // Handle SharePoint lookup field objects
                        if (rawValue && typeof rawValue === "object") {
                          // Single lookup field: { LookupId: 20, LookupValue: "Tennessee Department..." }
                          if ("LookupValue" in rawValue) {
                            fieldValue = rawValue.LookupValue;
                          } else if ("Title" in rawValue) {
                            fieldValue = rawValue.Title;
                          } else if ("LookupId" in rawValue) {
                            fieldValue = String(rawValue.LookupId);
                          }
                          // Multi-select lookup field: array of { LookupId, LookupValue }
                          else if (
                            Array.isArray(rawValue) &&
                            rawValue.length > 0
                          ) {
                            if ("LookupValue" in rawValue[0]) {
                              fieldValue = rawValue
                                .map((item: any) => item.LookupValue)
                                .join(", ");
                            } else if ("Title" in rawValue[0]) {
                              fieldValue = rawValue
                                .map((item: any) => item.Title)
                                .join(", ");
                            }
                          }
                        } else if (
                          rawValue !== undefined &&
                          rawValue !== null &&
                          rawValue !== ""
                        ) {
                          // Simple field value (string, number, boolean, date)
                          fieldValue = rawValue;
                        }

                        // Fallback to document properties or default value
                        if (!fieldValue) {
                          fieldValue =
                            (document as any)?.[field.name] ??
                            field.defaultValue ??
                            null;
                        }

                        // Debug logging for troubleshooting
                        if (!fieldValue || fieldValue === null) {
                          // Logging disabled
                        }

                        return (
                          <div key={field.name} style={{ marginBottom: 12 }}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                letterSpacing: 0.3,
                                textTransform: "uppercase",
                                color: "hsl(var(--muted-foreground))",
                                marginBottom: 4,
                              }}
                            >
                              {field.title || field.name}
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                color: "hsl(var(--foreground))",
                                wordBreak: "break-word",
                              }}
                            >
                              {renderFieldValue(field, fieldValue)}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}

              {/* Show message if no editable fields */}
              {documentStore.currentFormSchema?.fields &&
                documentStore.currentFormSchema.fields.filter(
                  (f) => f.type !== "Internal",
                ).length === 0 && (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "hsl(var(--muted-foreground))",
                    }}
                  >
                    This document has no fields available.
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default DocumentDetailPanel;
